import React, { useCallback, useMemo } from 'react'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS'
import SubscriptIcon from '@mui/icons-material/Subscript'
import SuperscriptIcon from '@mui/icons-material/Superscript'
import TitleIcon from '@mui/icons-material/Title'
import { Paper, Menu as MuiMenu, MenuItem } from '@mui/material'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { Editor, useEditorState } from '@tiptap/react'
// @ts-ignore
import { BubbleMenu } from '@tiptap/react/menus'

interface Props {
  editor: Editor
}

const Menu = ({ editor }: Props) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const editorState = useEditorState({
    editor,
    selector: ctx => {
      return {
        isBold: ctx.editor.isActive('bold') ?? false,
        canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
        isItalic: ctx.editor.isActive('italic') ?? false,
        canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
        isStrike: ctx.editor.isActive('strike') ?? false,
        canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
        isUnderline: ctx.editor.isActive('underline') ?? false,
        canUnderline: ctx.editor.can().chain().toggleUnderline().run() ?? false,
        isSubscript: ctx.editor.isActive('subscript') ?? false,
        canSubscript: ctx.editor.can().chain().toggleSubscript().run() ?? false,
        isSuperscript: ctx.editor.isActive('superscript') ?? false,
        canSuperscript: ctx.editor.can().chain().toggleSuperscript().run() ?? false,
        isParagraph: ctx.editor.isActive('paragraph') ?? false,
        isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
        canUndo: ctx.editor.can().chain().undo().run() ?? false,
        canRedo: ctx.editor.can().chain().redo().run() ?? false,
      }
    },
  })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const currentHeading = useMemo(() => {
    if (editorState.isHeading1) return 'H1'
    if (editorState.isHeading2) return 'H2'
    if (editorState.isHeading3) return 'H3'
    return 'Normal'
  }, [editorState.isHeading1, editorState.isHeading2, editorState.isHeading3])

  const formats = useMemo(() => {
    const activeFormats = []
    if (editorState.isBold) activeFormats.push('bold')
    if (editorState.isItalic) activeFormats.push('italic')
    if (editorState.isUnderline) activeFormats.push('underlined')
    if (editorState.isStrike) activeFormats.push('strikethrough')
    if (editorState.isSubscript) activeFormats.push('subscript')
    if (editorState.isSuperscript) activeFormats.push('superscript')
    if (editorState.isHeading1) activeFormats.push('heading')
    if (editorState.isHeading2) activeFormats.push('heading')
    if (editorState.isHeading3) activeFormats.push('heading')
    return activeFormats
  }, [
    editorState.isBold,
    editorState.isItalic,
    editorState.isStrike,
    editorState.isUnderline,
    editorState.isSubscript,
    editorState.isSuperscript,
    editorState.isHeading1,
    editorState.isHeading2,
    editorState.isHeading3,
  ])

  const handleFormat = useCallback(
    (event: React.MouseEvent<HTMLElement>, newFormats: string[]) => {
      if (newFormats.includes('bold')) {
        editor.chain().setMark('bold').focus().run()
      } else {
        editor.chain().unsetMark('bold').focus().run()
      }
      if (newFormats.includes('italic')) {
        editor.chain().setMark('italic').focus().run()
      } else {
        editor.chain().unsetMark('italic').focus().run()
      }
      if (newFormats.includes('underlined')) {
        editor.chain().setMark('underline').focus().run()
      } else {
        editor.chain().unsetMark('underline').focus().run()
      }
      if (newFormats.includes('strikethrough')) {
        editor.chain().setMark('strike').focus().run()
      } else {
        editor.chain().unsetMark('strike').focus().run()
      }
      if (newFormats.includes('subscript')) {
        editor.chain().toggleSubscript().focus().run()
      } else {
        editor.chain().unsetMark('subscript').focus().run()
      }
      if (newFormats.includes('superscript')) {
        editor.chain().toggleSuperscript().focus().run()
      } else {
        editor.chain().unsetMark('superscript').focus().run()
      }
    },
    [editor],
  )

  const handleHeadingClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }, [])

  const handleHeadingClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleHeadingSelect = useCallback(
    (level: number | null) => {
      if (level === null) {
        editor.chain().focus().setParagraph().run()
      } else {
        editor
          .chain()
          .focus()
          .toggleHeading({ level: level as never })
          .run()
      }
      handleHeadingClose()
    },
    [editor, handleHeadingClose],
  )

  return (
    <BubbleMenu editor={editor}>
      <Paper>
        <ToggleButtonGroup
          value={formats}
          onChange={handleFormat}
          aria-label="text formatting"
          size="small"
        >
          <ToggleButton value="bold" aria-label="bold" disabled={!editorState.canBold}>
            <FormatBoldIcon />
          </ToggleButton>
          <ToggleButton value="italic" aria-label="italic" disabled={!editorState.canItalic}>
            <FormatItalicIcon />
          </ToggleButton>
          <ToggleButton
            value="underlined"
            aria-label="underlined"
            disabled={!editorState.canStrike}
          >
            <FormatUnderlinedIcon />
          </ToggleButton>
          <ToggleButton
            value="strikethrough"
            aria-label="strikethrough"
            disabled={!editorState.canStrike}
          >
            <StrikethroughSIcon />
          </ToggleButton>
          <ToggleButton
            value="subscript"
            aria-label="subscript"
            disabled={!editorState.canSubscript}
          >
            <SubscriptIcon />
          </ToggleButton>
          <ToggleButton
            value="superscript"
            aria-label="superscript"
            disabled={!editorState.canSuperscript}
          >
            <SuperscriptIcon />
          </ToggleButton>
          <ToggleButton value="heading" aria-label="heading" onClick={handleHeadingClick}>
            <TitleIcon />
            <ArrowDropDownIcon />
          </ToggleButton>
        </ToggleButtonGroup>
        <MuiMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleHeadingClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem onClick={() => handleHeadingSelect(null)}>Normal</MenuItem>
          <MenuItem onClick={() => handleHeadingSelect(1)}>Heading 1</MenuItem>
          <MenuItem onClick={() => handleHeadingSelect(2)}>Heading 2</MenuItem>
          <MenuItem onClick={() => handleHeadingSelect(3)}>Heading 3</MenuItem>
        </MuiMenu>
      </Paper>
    </BubbleMenu>
  )
}

export default Menu
